/**
 * Landing backdrop (§7.1): a small hand-composed realm from the Kenney tiles — two keeps, a
 * river with a bridge, villages, forests and two warbands — slowly panning behind the title.
 * Decorative (hidden from assistive tech); static with reduced motion.
 */
import { memo, useMemo } from 'react';
import { Rng } from '@seven-dawns/shared';
import { STAMPS, TILES, UNIT_SPRITES, waterTile, BASES, BANNERS, type Stamp } from '../../game/assets/tiles';
import type { TileId } from '../../game/assets/sheets';
import { SpriteStack } from '../../ui/Sprite';
import styles from './Backdrop.module.css';

const COLS = 40;
const ROWS = 9;
const SCALE = 2;
const RIVER_ROW = 5;

type Cell = TileId[];

function compose(): Cell[][] {
  const rng = new Rng(7);
  const grid: Cell[][] = Array.from({ length: ROWS }, () =>
    Array.from({ length: COLS }, () => [
      rng.chance(0.2) ? rng.pick([TILES.grassTufts, TILES.grassFlowers]) : TILES.grass,
    ]),
  );
  const put = (col: number, row: number, tile: TileId, replace = false) => {
    const cell = grid[row]?.[col];
    if (!cell) return;
    if (replace) cell.splice(0, cell.length, tile);
    else cell.push(tile);
  };
  const stamp = (col: number, row: number, s: Stamp) =>
    s.forEach((line, dy) =>
      line.forEach((t, dx) => {
        if (t) put(col + dx, row + dy, t);
      }),
    );

  // River with a bridge.
  for (let c = 0; c < COLS; c++) put(c, RIVER_ROW, waterTile(5), true);
  put(17, RIVER_ROW, TILES.bridgeNS, true);
  put(31, RIVER_ROW, TILES.bridgeNS, true);
  // Roads down to the bridges.
  for (let r = 0; r < ROWS; r++) {
    if (r !== RIVER_ROW) {
      put(17, r, rng.pick(TILES.dirt), true);
      put(31, r, rng.pick(TILES.dirt), true);
    }
  }
  // Forests.
  const forests: [number, number, number, number, 'pine' | 'green' | 'autumn'][] = [
    [0, 6, 0, 4, 'pine'],
    [9, 13, 6, 8, 'green'],
    [22, 27, 0, 3, 'autumn'],
    [34, 39, 6, 8, 'pine'],
  ];
  for (const [c0, c1, r0, r1, kind] of forests) {
    for (let r = r0; r <= r1; r++)
      for (let c = c0; c <= c1; c++) {
        if (r === RIVER_ROW || !rng.chance(0.7)) continue;
        const tile =
          kind === 'pine'
            ? rng.pick(TILES.pine)
            : kind === 'green'
              ? rng.pick(TILES.treeGreen)
              : rng.pick(TILES.treeAutumn);
        put(c, r, tile, kind !== 'pine');
      }
  }
  // Keeps and villages.
  stamp(8, 0, STAMPS.keep);
  stamp(33, 0, STAMPS.houseGrey);
  stamp(19, 1, STAMPS.houseRed);
  stamp(24, 6, STAMPS.houseRed);
  stamp(2, 6, STAMPS.houseGrey);
  stamp(28, 6, STAMPS.watchtower);
  // Two warbands meeting at the bridge.
  const band = (col: number, row: number, base: TileId) => {
    (['infantry', 'archers', 'cavalry', 'infantry'] as const).forEach((u, i) => {
      put(col + (i % 2), row + Math.floor(i / 2), base);
      put(col + (i % 2), row + Math.floor(i / 2), UNIT_SPRITES[u]);
    });
  };
  band(14, 3, BASES.azure);
  band(19, 6, BASES.ember);
  put(13, 3, BANNERS.azure);
  put(21, 7, BANNERS.ember);
  return grid;
}

export const Backdrop = memo(function Backdrop() {
  const grid = useMemo(compose, []);
  const tile = 16 * SCALE;
  return (
    <div className={styles.backdrop} aria-hidden="true">
      <div className={styles.world} style={{ width: COLS * tile, height: ROWS * tile }}>
        {grid.map((row, r) =>
          row.map((cell, c) => (
            <span key={`${r}-${c}`} className={styles.cell} style={{ left: c * tile, top: r * tile }}>
              <SpriteStack tiles={cell} scale={SCALE} />
            </span>
          )),
        )}
      </div>
      <div className={styles.shade} />
    </div>
  );
});
